import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  FileText,
  Gift,
  Loader2,
  RefreshCw,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { StepIndicator } from '@/components/pos/StepIndicator';
import { MoneyValue } from '@/components/pos/MoneyValue';
import { uploadFocDocument } from '@/api/focDocument.api';
import {
  FOC_DOCUMENT_ACCEPT,
  focDocumentMimeType,
  focErrorMessage,
  formatFileSize,
  isFocDocumentImage,
  validateFocDocument,
} from '@/lib/focDocument';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { cartSelectors, useCartStore } from '@/stores/cart.store';
import { useShallow } from 'zustand/react/shallow';
import { useHotkeys } from '@/hooks/useHotkeys';

/**
 * FOC flow step 2 — attach the ONE supporting document (PDF / PNG / JPG,
 * max 10 MB) the free-of-charge sale must carry. The file is uploaded as soon
 * as it is picked (POST /foc-document); its id is kept on the cart and sent on
 * the sale as `focDocumentId`.
 */
export function FocDocumentPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const { lines, mode, visitDate, focDocument, setFocDocument } = useCartStore(
    useShallow((s) => ({
      lines: s.lines,
      mode: s.mode,
      visitDate: s.visitDate,
      focDocument: s.focDocument,
      setFocDocument: s.setFocDocument,
    })),
  );
  const cartCount = useCartStore(cartSelectors.lineCount);
  const subtotal = useCartStore(cartSelectors.subtotal);

  // Guards: an empty cart starts over on the FOC catalog; a regular cart has no
  // business here (its flow has no document step).
  useEffect(() => {
    if (cartCount === 0) navigate('/foc', { replace: true });
    else if (mode !== 'foc') navigate('/', { replace: true });
  }, [cartCount, mode, navigate]);

  const { mutate: upload, isPending } = useMutation({
    mutationFn: (file: File) => uploadFocDocument(file),
    onSuccess: (doc, file) => {
      const mimeType = doc.mimeType || focDocumentMimeType(file);
      setFocDocument({
        id: doc.id,
        name: doc.originalName || file.name,
        mimeType,
        sizeBytes: typeof doc.sizeBytes === 'number' ? doc.sizeBytes : file.size,
        previewUrl: isFocDocumentImage(mimeType) ? URL.createObjectURL(file) : undefined,
      });
      toast.success('Dokumen FOC terunggah.');
    },
    onError: (err: unknown) =>
      toast.error(
        focErrorMessage(err) ?? (err as { message?: string })?.message ?? 'Gagal mengunggah dokumen.',
      ),
  });

  /** Validate (type + size) then upload — shared by the picker and drag-and-drop. */
  const submitFile = (file: File) => {
    const problem = validateFocDocument(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    upload(file);
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so re-picking the SAME file still fires onChange.
    e.target.value = '';
    if (file) submitFile(file);
  };

  const onDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (isPending) return;
    const files = e.dataTransfer.files;
    if (files.length > 1) {
      toast.error('Unggah satu dokumen saja.');
      return;
    }
    const file = files[0];
    if (file) submitFile(file);
  };

  const pick = () => !isPending && fileInputRef.current?.click();
  const canProceed = Boolean(focDocument) && !isPending;
  const proceed = () => canProceed && navigate('/details');

  useHotkeys({
    f8: proceed,
    escape: () => navigate('/foc'),
  });

  if (cartCount === 0 || mode !== 'foc') return null;

  const isImage = focDocument ? isFocDocumentImage(focDocument.mimeType) : false;

  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col px-6 py-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <Button variant="ghost" className="gap-2" onClick={() => navigate('/foc')}>
          <ArrowLeft className="h-4 w-4" /> Kembali
        </Button>
        <StepIndicator flow="foc" current={1} />
      </div>

      <div className="grid min-h-0 flex-1 gap-6 lg:grid-cols-5">
        {/* Left — the document */}
        <div className="min-h-0 lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" /> Dokumen pendukung FOC
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Unggah satu dokumen dasar pemberian tiket gratis — misalnya surat undangan atau
                memo persetujuan. PDF, PNG, atau JPG, maksimal 10 MB.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {focDocument && !isPending ? (
                <div className="space-y-3 rounded-md border-2 border-success/40 bg-success/5 p-4">
                  <div className="flex items-start gap-3">
                    {isImage && focDocument.previewUrl ? (
                      <img
                        src={focDocument.previewUrl}
                        alt={`Pratinjau ${focDocument.name}`}
                        className="h-24 w-24 shrink-0 rounded-md border border-border bg-card object-cover"
                      />
                    ) : (
                      <span className="flex h-24 w-24 shrink-0 flex-col items-center justify-center gap-1 rounded-md border border-border bg-card text-crimson-700">
                        <FileText className="h-9 w-9" aria-hidden="true" />
                        <span className="text-[10px] font-bold uppercase tracking-wide text-ink-400">
                          {isImage ? 'Gambar' : 'PDF'}
                        </span>
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-success">
                        <Check className="h-3.5 w-3.5" /> Terunggah
                      </p>
                      <p className="mt-1 break-all font-medium text-ink-900">{focDocument.name}</p>
                      <p className="tnum text-xs text-ink-400">
                        {formatFileSize(focDocument.sizeBytes)}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={pick}>
                    <RefreshCw className="h-4 w-4" /> Ganti file
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={pick}
                  disabled={isPending}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  className={cn(
                    'flex w-full flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    dragOver
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-cream-50/60 hover:border-primary/40 hover:bg-secondary',
                    isPending && 'cursor-wait opacity-80',
                  )}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden="true" />
                      <span className="font-display text-lg font-semibold text-ink-900">
                        Mengunggah dokumen…
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-cream-100 text-crimson-700">
                        <Upload className="h-6 w-6" aria-hidden="true" />
                      </span>
                      <span className="font-display text-lg font-semibold text-ink-900">
                        Pilih dokumen
                      </span>
                      <span className="text-xs text-ink-400">
                        Klik atau seret file ke sini · PDF, PNG, JPG · maks. 10 MB
                      </span>
                    </>
                  )}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={FOC_DOCUMENT_ACCEPT}
                className="sr-only"
                onChange={onPickFile}
              />
            </CardContent>
          </Card>
        </div>

        {/* Right — what the document backs */}
        <div className="min-h-0 lg:col-span-2">
          <Card className="flex h-full flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-primary" /> Tiket FOC
              </CardTitle>
              <p className="text-sm text-muted-foreground">Kunjungan {formatDate(visitDate)}</p>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col">
              <ScrollArea className="flex-1">
                <ul className="space-y-2 pr-2">
                  {lines.map((l) => (
                    <li key={l.key} className="flex justify-between gap-2 text-sm">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink-900">{l.name}</span>
                        <span className="tnum text-xs text-ink-400">× {l.seats.length}</span>
                      </span>
                      <MoneyValue value={l.unitPrice * l.seats.length} className="text-sm" />
                    </li>
                  ))}
                </ul>
              </ScrollArea>
              <div className="mt-4 flex items-end justify-between border-t border-border pt-4">
                <span className="text-sm font-semibold uppercase tracking-wide text-ink-600">
                  Subtotal
                </span>
                <MoneyValue value={subtotal} emphasis className="text-2xl" />
              </div>
              <Button
                size="lg"
                variant="gold"
                className="mt-4 h-16 w-full text-xl"
                disabled={!canProceed}
                onClick={proceed}
              >
                Lanjut <ArrowRight className="h-6 w-6" />
                <kbd className="ml-1 rounded border border-ink-900/20 px-1.5 text-xs">F8</kbd>
              </Button>
              {!focDocument && (
                <p className="mt-2 text-center text-xs text-warning">
                  Unggah dokumen pendukung untuk melanjutkan.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
